/**
 * UpSurv 2.0 — surveyor access tokens (brief §3, DECISIONS D-005).
 *
 * The whole auth story is one secret per surveyor, delivered by SMS as part of
 * their PWA link. Three properties carry it, and all three are load-bearing:
 *
 *  1. **Minted backend-only.** Chip and Tim assign work by NAME. They never see,
 *     type, or handle a token — so nothing here is reachable from an admin form,
 *     and the plaintext is never put in an HTTP response body. It is returned
 *     from `generateAccessToken()` exactly once, travels straight into the SMS
 *     body, and is then unrecoverable.
 *  2. **Stored hashed.** `surveyors.token_hash` holds SHA-256 hex (see
 *     `hashToken` in src/db/queries.ts). A D1 leak yields digests, not links.
 *     The direct consequence: a token cannot be re-sent, only re-minted — see
 *     the note on rotation in functions/api/[[route]].ts.
 *  3. **256 bits from the CSPRNG.** `crypto.getRandomValues` in both target
 *     runtimes; `Math.random()` appears nowhere near this file.
 *
 * No Node APIs: `Buffer`, `node:crypto` and `btoa` are all avoided in favour of
 * a hand-rolled base64url so the same code runs in a Worker and in a browser.
 */

/** 32 bytes = 256 bits. Overkill for 2–5 surveyors, and it costs 43 characters. */
export const TOKEN_BYTES = 32;
export const TOKEN_ENTROPY_BITS = TOKEN_BYTES * 8;

/** base64url of 32 bytes: ceil(32 / 3) groups, unpadded. */
export const TOKEN_LENGTH = 43;

/** Leading characters kept in `surveyors.token_prefix` for support lookups. */
export const TOKEN_PREFIX_LENGTH = 8;

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Injected so tests can pin the bytes; production always gets the CSPRNG. */
export type RandomBytes = (length: number) => Uint8Array;

export const cryptoRandomBytes: RandomBytes = (length) =>
  crypto.getRandomValues(new Uint8Array(length));

/**
 * RFC 4648 §5 base64url, unpadded. Unpadded on purpose: the token lands in an
 * SMS and in a URL fragment, and `=` is percent-encoded by some clients, which
 * would silently corrupt the link.
 */
export function base64UrlEncode(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];

    out += BASE64URL_ALPHABET.charAt(b0 >> 2);
    out += BASE64URL_ALPHABET.charAt(((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4));
    if (b1 === undefined) break;
    out += BASE64URL_ALPHABET.charAt(((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6));
    if (b2 === undefined) break;
    out += BASE64URL_ALPHABET.charAt(b2 & 0x3f);
  }
  return out;
}

/**
 * Mint one access token. The return value is the ONLY time the plaintext
 * exists — hash it for storage, put it in the SMS, and let it fall out of
 * scope. Do not log it, do not return it to an API caller.
 */
export function generateAccessToken(random: RandomBytes = cryptoRandomBytes): string {
  const bytes = random(TOKEN_BYTES);
  if (bytes.length !== TOKEN_BYTES) {
    throw new Error(
      `generateAccessToken: random source returned ${bytes.length} bytes, expected ${TOKEN_BYTES}`,
    );
  }
  return base64UrlEncode(bytes);
}

/** Non-secret identifier for a token: enough to answer "which link is that?". */
export function tokenPrefix(rawToken: string): string {
  return rawToken.slice(0, TOKEN_PREFIX_LENGTH);
}

/**
 * Shape check only — a token is authenticated by hash lookup, never by this.
 * Its job is to reject obvious junk before it costs a SHA-256 and a D1 round
 * trip, and to keep an oversized header out of the query planner.
 */
export function looksLikeAccessToken(candidate: string): boolean {
  if (candidate.length !== TOKEN_LENGTH) return false;
  for (let i = 0; i < candidate.length; i++) {
    if (BASE64URL_ALPHABET.indexOf(candidate.charAt(i)) === -1) return false;
  }
  return true;
}

/**
 * Length-independent, value-independent comparison. Used for the admin secret,
 * which IS compared in code; the surveyor token is compared by D1 on a SHA-256
 * digest, so an attacker probing that path would need a preimage before a
 * timing signal told them anything.
 *
 * Always walks `max(a.length, b.length)` characters and folds the length
 * difference into the accumulator, so neither the outcome nor the operand
 * lengths are observable from how long this took.
 */
export function constantTimeEqual(a: string, b: string): boolean {
  const length = Math.max(a.length, b.length);
  let diff = a.length ^ b.length;
  for (let i = 0; i < length; i++) {
    // charCodeAt past the end is NaN; `|| 0` normalizes without branching on
    // which string ran out first.
    diff |= (a.charCodeAt(i) || 0) ^ (b.charCodeAt(i) || 0);
  }
  return diff === 0;
}

/**
 * `Authorization: Bearer <token>` → `<token>`.
 *
 * The scheme match is case-insensitive per RFC 7235; everything after the first
 * space is the credential, trimmed. Returns null for anything else — including
 * `Basic`, which must not be silently treated as a token.
 */
export function parseBearer(header: string | null | undefined): string | null {
  if (!header) return null;
  const match = /^Bearer[ \t]+(.+)$/i.exec(header.trim());
  const credential = match?.[1]?.trim();
  return credential ? credential : null;
}
